/**
 * El partido, sin una sola linea de render.
 *
 * Vive aparte de `match.ts` por una razon concreta: antes el bucle de tick solo
 * existia dentro del objeto de Pixi, asi que el script de calibracion y
 * cualquier test tenian que **reescribir** el orden del tick a mano. Dos copias
 * del orden mas delicado del juego, condenadas a divergir. Ahora hay una sola,
 * y lo que se mide es exactamente lo que se juega.
 *
 * Lo visual entra por `SimHooks`: la simulacion avisa de lo que ha pasado y
 * quien quiera lo pinta. Sin hooks, funciona igual.
 */
import {
  ASSIST_PROFILES,
  DEFAULT_ASSIST,
  BODY_CHECK,
  checkOffsideOnReceive,
  offsideRestartSpot,
  wallRetreatPos,
  KICK,
  MATCH,
  MATCH_FORMAT,
  OFFSIDE,
  FIELD,
  FIRST_TIME,
  fieldPlayersPerTeam,
  goalMouth,
  type AssistLevel,
  type AssistProfile,
  type Ball,
  type FirstTimeSwing,
  type InputFrame,
  type MatchState,
  type Player,
  type PlayerTalent,
  type Possession,
  type TeamSide,
  type Vec2,
} from "@golazo/shared";
import { createAiController, type AiController, type AiWorld } from "../ai/brain.js";
import { paceScale } from "../ai/skill.js";
import { createTacticsWorld } from "../ai/tactics.js";
import { closestPointOnSegment, len2 } from "../core/math.js";
import { createRandom } from "../core/random.js";
import { createBall } from "../entities/ball.js";
import { createAllPlayers, controlledPlayerId, resetPlayers } from "../entities/player.js";
import {
  collideBallWithPlayers,
  collideBallWithWalls,
  resolvePlayerCollisions,
} from "../physics/collisions.js";
import { confineToField, stepBall, stepPlayer } from "../physics/step.js";
import { tryGoalkeeperDive, tryGoalkeeperJump } from "../physics/goalkeeper.js";
import { forgiveTrap } from "./humanAssist.js";
import { allowsTrapShoot } from "./crossFinish.js";
import {
  findFirstTimeWindow,
  firstTimePower,
  firstTimeShotStyle,
  firstTimeSpreadScale,
  gradeOf,
  type FirstTimeGrade,
} from "./firstTime.js";
import { applyBodyCheck } from "./bodyCheck.js";
import { applyBackNutmeg, applyCut, applyFake, applyNutmeg, createPossession, findOwner, kickBall, shouldCross, updatePossession, type PossessionLossKind } from "./possession.js";
import { aftertouchSpin, type KickKind } from "./kick.js";

/** Margen (u) a partir del cual el balon "ha llegado" a un jugador y se acaba el aftertouch. */
const AFTERTOUCH_REACH = 10;
/** Al portero, con el brazo: se acaba antes de que llegue a la estirada. */
const AFTERTOUCH_GK_REACH = 48;
import { dexp } from "../core/detMath.js";
import {
  captureSnapshot,
  restoreSnapshot,
  varStage,
  varReplayProgress,
  ReplayBuffer,
  type KickTrack,
  type ReplaySnapshot,
} from "./replay.js";

const HUMAN_SIDE: TeamSide = "home";

export interface SimHumanSeat {
  playerId: string;
  nickname: string;
}

export interface SimOptions {
  seed?: number;
  shirtNumber?: number;
  assist?: AssistLevel;
  /** Asientos humanos (online). El portero NUNCA se controla. */
  humanSeats?: SimHumanSeat[];
  /** Apodo del jugador local. En online lo trae `humanSeats`; aqui, el menu. */
  nickname?: string;
  /** Asiento que controla este cliente. */
  localPlayerId?: string;
  /**
   * Sin humano: los dos equipos completos los lleva la IA.
   *
   * No es solo "ignorar el teclado". La capa tactica excluye al humano del
   * reparto de tareas a proposito (no puede fiarse de que vaya a presionar), y
   * si se deja activo en una simulacion automatica ese jugador queda de adorno
   * y su equipo juega en inferioridad. Los partidos medidos asi mienten.
   */
  autopilot?: boolean;
  /**
   * Mantiene vivo el cerebro de la IA para los jugadores controlados desde
   * fuera, aunque su decision se descarte y no llegue a ejecutarse.
   *
   * Por defecto esos cerebros se saltan (su salida se tirararia), y entonces
   * `aiFrameFor` devuelve datos rancios para ellos. Con esto encendido se puede
   * consultar en todo momento que habria hecho la IA en la situacion actual,
   * la controle quien la controle: sirve para sugerencias al jugador humano,
   * depuracion tactica o cualquier consumidor externo que necesite la opinion
   * de la IA sin cederle el mando.
   *
   * Cuesta CPU (corren todos los cerebros) y puede mover ligeramente la capa
   * tactica compartida, asi que va apagado por defecto.
   */
  keepAiAwake?: boolean;
  /**
   * CARRERA (solo offline): asiento del humano en su equipo, segun su
   * posicion. Sin definir, el de siempre del modo.
   */
  humanSeatIndex?: number;
  /**
   * CARRERA (solo offline): el lado del humano. De visitante tu club juega a
   * la derecha, como en un partido de verdad. Sin definir, el local.
   */
  humanSide?: TeamSide;
  /**
   * CARRERA (solo offline): talento de cada equipo y del humano, todo cerca
   * de 1 (ver `career/talent.ts`). El online nunca lo pasa: sin esto la
   * simulacion es identica, bit a bit, a la de siempre.
   */
  talent?: { home?: PlayerTalent; away?: PlayerTalent; human?: PlayerTalent };
  /**
   * CARRERA (solo offline): nivel de la IA de cada equipo, 0 (barrio, torpe)
   * a 1 (la de siempre). Ver `ai/skill.ts`. `human` solo lo usa la sonda, para
   * poner un "novato" en tu asiento en piloto automatico: en un partido de
   * verdad tu asiento no lleva IA y no se le pone nada.
   */
  aiSkill?: { home?: number; away?: number; human?: number };
  /**
   * SOLO SONDAS: `false` apaga la lectura del marcador y el reloj en la IA
   * (`ai/situation.ts`), para medir lo que cambia. Todo partido de verdad, el
   * online incluido, la lleva encendida.
   */
  readScore?: boolean;
}

/** Avisos hacia la capa visual. Ninguno afecta a la simulacion. */
export interface SimHooks {
  /**
   * Se ha fijado la direccion del golpeo al empezar a cargar. Para cualquier
   * humano (dos en el mismo ordenador tienen cada uno su mira): quien escucha
   * mira `playerId`.
   */
  onShotLock?(dir: Vec2, kind: KickKind, playerId: string): void;
  /** La carga ha terminado (golpeo, finta o cancelacion). */
  onShotRelease?(playerId: string): void;
  /** El humano ha pasado a controlar otro jugador. */
  onDash?(x: number, y: number, fx?: number, fy?: number, side?: TeamSide): void;
  onTackle?(x: number, y: number): void;
  onGoal?(scorer: TeamSide): void;
  /**
   * El balon ha entrado: por que porteria, a que altura y con que velocidad.
   * Separado de `onGoal` porque es puro adorno (la red) y `onGoal` lo escuchan
   * el marcador y el audio, que no necesitan saber nada de geometria.
   */
  onGoalNet?(conceded: TeamSide, y: number, speed: number): void;
  /** Fuera de juego pitado contra este equipo. */
  onOffside?(team: TeamSide): void;
  /** Golpeo ejecutado. Es la unica forma fiable de contar tiros: deducirlo de
   *  la velocidad de salida falla, porque cada modo escala el golpeo. */
  onKick?(kind: KickKind, by: Player, power: number): void;
  /** Choque fuerte en la linea de fondo (banda). */
  onSidelineBump?(x: number, y: number, intensity: number): void;
  /** Balon contra un palo de porteria. */
  onPostHit?(x: number, y: number, speed: number): void;
  /** Balon levantado: malabar parado o sombrero en carrera. */
  /** Caño a un rival. */
  onNutmeg?(by: Player, victim: Player): void;
  /** Poner el cuerpo: has embestido a un rival sin llevar el balon. */
  onBodyCheck?(by: Player, victim: Player): void;
  /**
   * El cuerpo ha rebotado: el rival llevaba el balon y tenia el cuerpo
   * plantado. `by` es el que sale despedido, `victim` el que aguanto.
   */
  onBodyBlock?(by: Player, victim: Player): void;
  /** El portero se ha lanzado. */
  onDive?(gk: Player): void;
  /**
   * El dueno del balon lo ha perdido. `by` es null en `carry`: nadie se lo
   * quito, se le escapo conduciendo.
   */
  onPossessionLost?(kind: PossessionLossKind, from: Player, by: Player | null): void;
  /** Corte seco lateral. */
  onCut?(by: Player): void;
  /**
   * Golpeo de primera. `grade` permite premiar el timing con feedback distinto:
   * es donde vive casi toda la sensacion de la mecanica.
   */
  onFirstTime?(grade: FirstTimeGrade, by: Player, kind: KickKind, quality: number): void;
  /** Se armo el remate y el balon nunca llego: pegar al aire. */
  onFirstTimeWhiff?(by: Player): void;
}

export interface Simulation {
  /**
   * Ventana de primera abierta AHORA para el jugador que controla el humano.
   * La expone la simulacion (no la calcula el render) para que la señal visual
   * y la mecanica no puedan desincronizarse: se mira exactamente lo mismo.
   */
  /** Ventana de golpeo de primera del jugador (`humanId` si no se dice cual). */
  firstTimeWindow(playerId?: string): { quality: number; timeToContact: number } | null;
  /**
   * Corta la celebracion y la repeticion: al saque, ya.
   *
   * **Solo sin red.** Muta `state.goalPause`, que es estado de la simulacion:
   * si un cliente lo saltara y el otro no, los dos partidos dejarian de ser el
   * mismo desde ese tick. Quien lo llama tiene que comprobar que no hay
   * lockstep — aqui no se puede comprobar porque la simulacion no sabe si esta
   * en red.
   *
   * Deja la cuenta atras en pie a proposito: sirve para colocarse antes del
   * saque, y saltarsela seria empezar a jugar sin estar listo.
   */
  skipGoalPause(): void;
  readonly players: readonly Player[];
  readonly ball: Ball;
  readonly possession: Possession;
  readonly state: MatchState;
  readonly brains: readonly AiController[];
  readonly aiWorld: AiWorld;
  /**
   * Cuanto ha cedido la red bajo el balon, en unidades de mundo.
   *
   * La expone la simulacion —igual que `firstTimeWindow`— para que el dibujo
   * del bolsillo y la fisica que frena el balon no puedan discrepar. Volver a
   * calcularlo en el render es como se acaban contando dos historias.
   */
  readonly netGive: number;
  /**
   * Ultimo `InputFrame` que decidio la IA para ese jugador, solo lectura.
   *
   * Es la unica forma de ver QUE decidio la IA sin volver a llamar a
   * `brain.update`, que tiene estado interno y avanzaria dos veces. Para
   * jugadores controlados desde fuera hace falta ademas `keepAiAwake`.
   */
  aiFrameFor(id: string): InputFrame | null;
  /**
   * El aspecto grabado de los jugadores (ver `LOOK` en `replay.ts`) mientras se
   * enseña una repeticion; `null` el resto del tiempo. El render lo usa en vez
   * del estado en vivo, para que el clip repita lo que se vio en la jugada.
   */
  replayLook(): Float32Array | null;
  readonly assist: AssistProfile;
  /** Jugador que lleva el humano ahora mismo. */
  readonly humanId: string;
  /** Avanza un tick. `frame` es el input humano; `null` = todo IA. */
  tick(dt: number, frame: InputFrame | null): void;
  /**
   * Avanza un tick con varios jugadores de campo controlados externamente.
   * Los porteros se ignoran aunque aparezcan en el mapa: siempre son bots.
   */
  tickMulti(dt: number, frames: ReadonlyMap<string, InputFrame>): void;
}

export function createSimulation(options: SimOptions = {}, hooks: SimHooks = {}): Simulation {
  const seed = options.seed ?? (Date.now() >>> 0);
  const assist: AssistProfile =
    ASSIST_PROFILES[options.assist ?? DEFAULT_ASSIST] ?? ASSIST_PROFILES[DEFAULT_ASSIST];

  const humanSide = options.humanSide ?? HUMAN_SIDE;
  const players: Player[] = createAllPlayers({
    humanShirtNumber: options.shirtNumber,
    controlledIndex: options.humanSeatIndex,
    humanSide,
  });

  for (const seat of options.humanSeats ?? []) {
    const p = players.find((x) => x.id === seat.playerId);
    if (p && p.role !== "gk") {
      p.nickname = seat.nickname;
      p.human = true;
    }
  }

  let humanId =
    options.localPlayerId ??
    (options.humanSeatIndex !== undefined ? `${humanSide}-${options.humanSeatIndex}` : controlledPlayerId(humanSide));
  {
    // El portero siempre es bot: si el asiento local fuera GK, salta a campo.
    const hp = players.find((p) => p.id === humanId);
    if (!hp || hp.role === "gk") {
      const fallback =
        players.find((p) => p.side === (hp?.side ?? humanSide) && p.role !== "gk") ??
        players.find((p) => p.role !== "gk");
      if (!fallback) throw new Error("No hay jugador de campo controlable");
      humanId = fallback.id;
    }
  }

  {
    // El asiento local cuenta como humano aunque no venga en `humanSeats`: en
    // partido local no hay lista de asientos. Se marca tambien en autopilot,
    // porque representa "el sitio donde va una persona" — y es lo que hace que
    // el auditor mida lo que de verdad sentiria ese jugador.
    const local = players.find((p) => p.id === humanId);
    if (local && local.role !== "gk") {
      local.human = true;
      /*
       * Tu apodo, tambien en partido LOCAL.
       *
       * `humanSeats` solo lo rellena el online (viene de los asientos de la
       * sala), asi que jugando en local el apodo no llegaba nunca y la tabla
       * final caia a "Jugador 10": el numero en vez del nombre, que es justo lo
       * que se reporto. Se aplica aqui, despues de resolver `humanId`, que es
       * el unico punto donde ya se sabe QUE jugador eres.
       */
      if (options.nickname) local.nickname = options.nickname;
    }
  }


  /*
   * El talento de la carrera. Solo se toca a quien lo tiene: multiplicar por 1
   * seria exacto, pero no tocar nada es la unica garantia que no hay que
   * demostrar para el lockstep.
   */
  if (options.talent) {
    for (const p of players) {
      const talent = p.id === humanId ? (options.talent.human ?? options.talent[p.side]) : options.talent[p.side];
      if (!talent) continue;
      p.talent = talent;
      p.speedMultiplier *= talent.pace;
    }
  }
  // Antes de crear los cerebros: el nivel se lee al crearlos.
  if (options.aiSkill) {
    for (const p of players) {
      // Otra persona (dos en el mismo ordenador) no es IA: la dificultad no la
      // hace mas torpe ni mas lenta.
      if (p.human && p.id !== humanId) continue;
      const skill = p.id === humanId ? options.aiSkill.human : options.aiSkill[p.side];
      if (skill === undefined) continue;
      p.aiSkill = skill;
      // El del barrio corre menos. Tu asiento no: aunque la sonda le ponga
      // nivel para imitar a un novato, un novato no es mas lento.
      if (p.id !== humanId) p.speedMultiplier *= paceScale(p);
    }
  }

  const ball: Ball = createBall();
  const possession: Possession = createPossession();

  const state: MatchState = {
    phase: "playing",
    timeRemaining: MATCH.DURATION,
    score: { home: 0, away: 0 },
    goalPause: 0,
    lastScorer: null,
    offsideTeam: null,
    offsideReview: null,
    goldenGoal: false,
    period: 1,
    periods: MATCH_FORMAT.periods,
    breakPause: 0,
  };

  /**
   * Corre el reloj. Al llegar a cero el partido NO acaba si van empatados:
   * entra en gol de oro y se sigue jugando hasta que alguien marque.
   *
   * Vive en una funcion porque el reloj se descuenta en dos salidas distintas
   * del tick (la normal y la del fuera de juego), y con la regla duplicada la
   * segunda se olvidaria del gol de oro — que es justo el fallo que dejaria un
   * partido empatado acabandose por un fuera de juego.
   */
  /**
   * ¿El tiempo agotado significa FINAL, o solo fin de este tiempo?
   *
   * Vive en una funcion porque la pregunta se hace en DOS salidas distintas del
   * tick: la normal (`tickClock`) y la del final de la celebracion de un gol.
   * El comentario de `tickClock` ya avisaba de esta trampa para el gol de oro —
   * y al añadir los tiempos volvio a picar: la segunda salida terminaba el
   * partido al acabar el primer tiempo, sin descanso ni segundo.
   */
  const timeUpEndsMatch = (): boolean => {
    if (state.goldenGoal) return true;
    if (state.period < state.periods) return false;
    return state.score.home !== state.score.away || MATCH_FORMAT.onDraw === "draw";
  };

  /**
   * Empieza el descanso: saque colocado YA y todo el mundo quieto.
   *
   * El saque se coloca al ENTRAR y no al salir, y es lo que arreglo el bucle
   * infinito: dejando solo la cuenta atras, el balon seguia vivo donde estaba y
   * volvia a entrar en la porteria una y otra vez — gol, descanso, gol,
   * descanso, sin que el partido avanzara nunca de tiempo.
   *
   * Saca el que va perdiendo; empatados, el local. Es la convencion de siempre:
   * el segundo tiempo lo empieza el que no empezo el primero.
   */
  const beginBreak = (): void => {
    state.breakPause = MATCH.HALF_TIME;
    kickoffFromCentre(state.score.home > state.score.away ? "away" : "home");
  };

  const tickClock = (dt: number): void => {
    if (state.goldenGoal) return;

    /*
     * El descanso entre tiempos.
     *
     * Es una pausa como la del gol: el reloj del PARTIDO no corre y nadie se
     * mueve, pero la fase sigue siendo "playing". Se hace asi y no con una
     * fase nueva porque media docena de sitios miran `phase` para decidir si
     * hay juego, y añadir un valor obligaria a repasarlos todos para una pausa
     * de cuatro segundos.
     */
    if (state.breakPause > 0) {
      state.breakPause -= dt;
      if (state.breakPause <= 0) {
        state.breakPause = 0;
        state.period += 1;
        state.timeRemaining = MATCH.DURATION;
      }
      return;
    }

    state.timeRemaining -= dt;
    if (state.timeRemaining > 0) return;
    state.timeRemaining = 0;

    /*
     * ¿Se acaba el partido, o solo este tiempo?
     *
     * Con varios tiempos, llegar a cero no significa el final: se descansa y
     * se sigue. El gol de oro y el empate solo se deciden en el ULTIMO.
     */
    if (state.period < state.periods) {
      beginBreak();
      return;
    }

    if (state.score.home === state.score.away && MATCH_FORMAT.onDraw === "golden") {
      /*
       * Empate al final del ultimo tiempo. Antes el gol de oro era OBLIGATORIO
       * y un partido no podia acabar empatado ni queriendo; ahora es una
       * eleccion, y la otra rama deja que termine 0-0.
       */
      state.goldenGoal = true;
      return;
    }
    // Si el gol que desempata cae en el ultimo tick, el partido acaba al
    // terminar la celebracion, no en mitad de ella: poner "ended" aqui se
    // comia el zoom, el cartel y la repeticion del gol de la victoria.
    if (state.phase !== "goal") state.phase = "ended";
  };

  const autopilot = options.autopilot === true;
  const keepAiAwake = options.keepAiAwake === true;
  /**
   * El jugador que la SIMULACION trata como "el humano" en este tick.
   *
   * No es `humanId`. `humanId` es a quien sigue la camara y de quien se dibuja
   * la mira, y en red cambia de un cliente a otro. Si la logica del partido
   * leyera eso, dos maquinas con los mismos inputs simularian partidos
   * distintos — y el que entra a mitad, que reproduce lo que el anfitrion jugo
   * solo, lo haria con otro jugador como protagonista.
   *
   * Asi que sale SOLO de los inputs: con `tick(frame)` es el jugador local (en
   * local no hay mas); con `tickMulti` y un unico asiento humano, ese asiento;
   * con varios, nadie (cada uno va por su camino simetrico).
   */
  let controlledId: string | null = humanId;
  /** Id que ve la capa tactica: `null` cuando no hay humano de quien desconfiar. */
  const tacticalHumanId = (): string | null => (autopilot ? null : controlledId);
  /**
   * Con `autopilot` NADIE carga el golpeo: no hay humano que sujete el boton.
   *
   * `humanId` existe siempre (es un id calculado, `home-<controlledIndex>`),
   * asi que sin esta guarda el jugador que ocupa ese indice entraba igualmente
   * en la maquinaria de carga aunque lo llevara la IA o una politica RL. Su
   * `pressed` de tiro/pase abria una carga que se soltaba en el mismo tick con
   * `charge = 0`, y `chargeAndKick` la resolvia como corte/finta: el golpeo
   * nunca ocurria y `onKick` nunca se emitia. En RL eso dejaba mudo justo a un
   * agente por equipo (home-1 en 3v3, home-3 en 7v7): sus tiros y pases se
   * convertian en cortes, sin recompensa de tiro ni de pase.
   */
  const humanCharges = !autopilot;

  const random = createRandom(seed);
  const { world: tactics, update: updateTactics } = createTacticsWorld({
    readScore: options.readScore,
  });
  const aiWorld: AiWorld = { players, ball, possession, humanId: tacticalHumanId(), tactics };
  const brains = players.map((p) => createAiController(p, random));
  const aiFrames = new Map<string, InputFrame>();

  /**
   * Quien esta cargando un golpeo, o nadie. Un solo estado: el TIPO ya no se
   * elige al pulsar, se decide al soltar segun la carga (`KICK.PASS_MAX_CHARGE`).
   *
   * Es un id y no un booleano "del humano" por el online: en un partido en red
   * cada cliente tiene un `humanId` distinto, y con un booleano solo el dueño
   * del asiento cargaba su golpeo — en la pantalla del otro ese jugador no
   * chutaba nunca y los dos partidos se separaban en el primer toque. Solo
   * carga quien lleva el balon, asi que nunca hay dos a la vez.
   */
  let chargingId: string | null = null;
  let lastHumanOwnerId: string | null = null;
  /** Para detectar cuando se suelta Shift mientras se sigue siendo el dueno. */
  let concedingSide: TeamSide | null = null;
  /**
   * El saque de este gol ya esta colocado.
   *
   * La cuenta atras dura ~200 ticks y `kickoffFromGk` es destructivo (devuelve a
   * todo el mundo al spawn y pega el balon al portero): sin esta bandera se
   * ejecutaria en cada uno de ellos, y lo que se ve como una pausa serian 200
   * saques seguidos.
   */
  let restartPlaced = false;
  let offsideSpot: Vec2 | null = null;
  /**
   * Como estaba el campo cuando se pito el fuera de juego.
   *
   * La revision mueve a todo el mundo —primero la repeticion, luego el
   * congelado en el instante del pase—, asi que sin esto el saque se jugaria
   * desde unas posiciones de hace tres segundos.
   */
  let offsideFreeze: ReplaySnapshot | null = null;
  /** Posiciones del instante del pase, para el congelado con la linea. */
  let offsideSnapshotPositions: ReadonlyMap<string, { x: number; y: number }> | null = null;
  let offsideSnapshotBall: Vec2 | null = null;
  const replay = new ReplayBuffer();
  /** Esta tick se ha enseñado un fotograma de repeticion (gol o VAR). */
  let showingReplay = false;
  /*
   * Lo pulsado del golpeo por cada jugador, para grabarlo con la repeticion.
   * No es estado del partido: solo lo lee el clip. Ver `LOOK` en `replay.ts`.
   */
  const kicks: KickTrack = {
    held: new Uint8Array(players.length),
    since: new Float32Array(players.length).fill(99),
  };
  const trackKicks = (frameOf: (player: Player) => InputFrame | null, dt: number): void => {
    for (let i = 0; i < players.length; i++) {
      const f = frameOf(players[i]!);
      kicks.held[i] = f?.held.has("kick") ? 1 : 0;
      kicks.since[i] = f?.pressed.has("kick") ? 0 : Math.min(99, kicks.since[i]! + dt);
    }
  };

  const human = (): Player => players.find((p) => p.id === (controlledId ?? humanId))!;

  const lockKickDirection = (player: Player, frame: InputFrame, kind: KickKind): void => {
    const dir = frame.move.x !== 0 || frame.move.y !== 0 ? frame.move : player.facing;
    const len = len2(dir.x, dir.y) || 1;
    player.aimDir.x = dir.x / len;
    player.aimDir.y = dir.y / len;
    player.chargeSteer = true;
    // El gesto empieza de cero con cada carga: lo que comba es lo que haces
    // DENTRO de este disparo, no lo que venias haciendo antes.
    player.aimSweep = 0;
    // La mira es de TU jugador: la carga de otro humano no se dibuja aqui.
    if (player.id === humanId || player.human) {
      hooks.onShotLock?.({ x: player.aimDir.x, y: player.aimDir.y }, kind, player.id);
    }
  };

  const beginChargeIfPressed = (player: Player, frame: InputFrame): void => {
    const trapVolley = player.trapTime > 0 && allowsTrapShoot(possession, player);
    if (player.trapTime > 0 && !trapVolley) return;
    if (chargingId !== null) return;
    if (frame.pressed.has("kick") || frame.pressed.has("pass") || frame.pressed.has("shoot")) {
      chargingId = player.id;
      // La mira se bloquea como "shoot" porque es la que dibuja el arco de
      // comba; el tipo real se resuelve al soltar.
      lockKickDirection(player, frame, "shoot");
    }
  };

  /*
   * AFTERTOUCH (`KICK.AFTERTOUCH_*`): tras un disparo de una persona, lo que
   * pida de lado en los instantes siguientes dobla el balon. Estado de la
   * simulacion (sale del `InputFrame` de quien chuto, igual en todas las
   * maquinas); se acaba solo al llegar el balon a alguien.
   */
  let aftertouch: { playerId: string; timeLeft: number; added: number } | null = null;

  /** ¿Ha llegado el balon a otro jugador? Al portero se le da el brazo de margen. */
  const ballReachedSomeone = (kickerId: string): boolean =>
    players.some(
      (p) =>
        p.id !== kickerId &&
        len2(p.pos.x - ball.pos.x, p.pos.y - ball.pos.y) <
          p.radius + ball.radius + (p.role === "gk" ? AFTERTOUCH_GK_REACH : AFTERTOUCH_REACH),
    );

  const applyAftertouch = (dt: number, frameFor: (player: Player) => InputFrame | null): void => {
    if (!aftertouch) return;
    aftertouch.timeLeft -= dt;
    const kicker = players.find((p) => p.id === aftertouch!.playerId);
    const speed = len2(ball.vel.x, ball.vel.y);
    if (
      !kicker ||
      aftertouch.timeLeft <= 0 ||
      possession.ownerId !== null ||
      speed < KICK.AFTERTOUCH_MIN_SPEED ||
      ballReachedSomeone(kicker.id)
    ) {
      aftertouch = null;
      return;
    }
    const frame = frameFor(kicker);
    if (!frame) return;
    const delta = aftertouchSpin(ball.vel.x / speed, ball.vel.y / speed, frame.move, dt, aftertouch.added);
    ball.spin += delta;
    aftertouch.added += delta;
  };

  const cancelCharge = (player: Player): void => {
    player.charge = 0;
    player.chargeSteer = false;
    if (chargingId === player.id) chargingId = null;
    if (player.id === humanId || player.human) hooks.onShotRelease?.(player.id);
  };

  const chargeAndKick = (player: Player, frame: InputFrame, dt: number): void => {
    const trapVolley = player.trapTime > 0 && allowsTrapShoot(possession, player);
    if (player.trapTime > 0) {
      if (!trapVolley) {
        cancelCharge(player);
        return;
      }
    }

    if (chargingId !== player.id) {
      player.chargeSteer = false;
      return;
    }

    if (frame.held.has("kick") || frame.held.has("pass") || frame.held.has("shoot")) {
      player.charge = Math.min(player.charge + dt / KICK.CHARGE_TIME, 1);
      player.chargeSteer = true;
      return;
    }

    /*
     * UN TOQUE ES UN PASE. Siempre.
     *
     * Aqui vivia el atajo de la finta: soltar con `charge <= 0,12` hacia un
     * amago o un corte en vez de golpear. Con `CHARGE_TIME` en 0,42 s eso son
     * los primeros 50 ms — o sea, CUALQUIER toque corto, y en tactil un toque
     * corto es lo normal. Peor todavia: si el dedo entra y sale dentro del
     * mismo tick, la carga se queda en 0 y la finta era segura al 100%.
     *
     * El resultado era el sintoma exacto que se reporto: "si solo quiero dar un
     * pase, toco el boton y hace un dash sin golpear". El gesto mas usado del
     * juego —pasar— estaba secuestrado por el menos usado.
     *
     * La finta no se pierde: la IA la ejecuta por su propia via
     * (`pressed.has("fake")`, mas abajo), que es de donde salia de verdad en
     * los partidos. Lo que desaparece es el atajo del humano, que nadie pedia y
     * todo el mundo disparaba sin querer.
     */

    const opponents = players.filter((p) => p.side !== player.side);
    /*
     * LA CARGA DECIDE EL GOLPEO.
     *
     * Un toque sale como pase y una carga larga como disparo, sin nada que
     * adivine tu intencion: el mismo gesto, la misma direccion, y lo unico que
     * cambia es cuanto has mantenido el boton. Antes eran dos botones, o sea
     * que habia que decidir el TIPO antes de saber cuanta fuerza querias.
     */
    let kind: KickKind = player.charge <= KICK.PASS_MAX_CHARGE ? "pass" : "shoot";
    if (kind === "pass" && shouldCross(player, player.aimDir)) kind = "cross";
    // Sin iman: el golpeo sale exactamente a donde apuntas. Lo unico que
    // separa la direccion pedida de la real es el castigo por golpear cruzado
    // a la carrera, que es determinista y se aplica dentro de `kickBall`.
    const dir = player.aimDir;

    kickBall(ball, player, possession, {
      kind,
      dir,
      power: player.charge,
      aiming: true,
      opponents,
      assistSpreadScale: assist.spreadScale,
    }, players);
    hooks.onKick?.(kind, player, player.charge);
    // Solo el disparo: un pase va a un compañero, y doblarlo despues lo estropea.
    aftertouch = kind === "shoot" && KICK.AFTERTOUCH_TIME > 0 ? { playerId: player.id, timeLeft: KICK.AFTERTOUCH_TIME, added: 0 } : null;
    cancelCharge(player);
  };

  const resetNet = (): void => {
    netGive = 0;
    netGiveVel = 0;
    netHolding = false;
  };

  /**
   * Saque de centro tras encajar. Como en el futbol.
   *
   * Se llamaba `kickoffFromGk` y hacia lo que decia: dejaba el balon en los
   * pies del PORTERO, junto a su propia porteria. Eso no es un saque de centro,
   * es un saque de puerta, y en lados chicos era un regalo medido:
   *
   *   2v2  65 saques -> el balon acaba en el rival en los 65 (100%)
   *   3v3  52 saques -> en 16 (31%)
   *
   * La razon es geometrica y no se arregla afinando al portero: con un solo
   * companero de campo, su unica salida esta marcada por el unico rival de
   * campo, y el despeje largo cae donde ese rival ya esta. No hay pase bueno
   * que dar, asi que la respuesta no era "que el portero elija mejor" sino
   * "que no saque el portero".
   *
   * Ahora saca el jugador de campo desde el centro, con los rivales apartados
   * un instante — que es exactamente la regla de verdad: el balon en el punto
   * central y el contrario fuera del circulo hasta que se toca.
   */
  const kickoffFromCentre = (conceded: TeamSide): void => {
    // El paño se olvida del gol anterior: si no, el saque heredaria una red a
    // medio hundir y el proximo balon entraria en un bolsillo que ya existia.
    resetNet();
    resetPlayers(players);
    const centre = { x: FIELD.WIDTH / 2, y: FIELD.HEIGHT / 2 };
    // Saca un jugador de CAMPO, el que le pille mas cerca del centro. Nunca el
    // portero: sacar desde la porteria es lo que regalaba el balon.
    const field = players.filter((p) => p.side === conceded && p.role !== "gk");
    const taker = field.reduce((best, p) =>
      len2(p.pos.x - centre.x, p.pos.y - centre.y) <
      len2(best.pos.x - centre.x, best.pos.y - centre.y)
        ? p
        : best,
    );

    // Se le pone al pie, en el centro.
    taker.pos.x = centre.x - (conceded === "home" ? 1 : -1) * (taker.radius + ball.radius + 2);
    taker.pos.y = centre.y;
    taker.prevPos.x = taker.pos.x;
    taker.prevPos.y = taker.pos.y;
    taker.vel.x = 0;
    taker.vel.y = 0;
    ball.pos.x = centre.x;
    ball.pos.y = centre.y;
    ball.prevPos.x = ball.pos.x;
    ball.prevPos.y = ball.pos.y;
    ball.vel.x = 0;
    ball.vel.y = 0;
    ball.spin = 0;
    ball.topSpin = 0;
    ball.loft = 0;
    ball.prevLoft = 0;
    ball.loftVel = 0;
    ball.unstoppable = false;
    possession.ownerId = taker.id;
    /*
     * Y queda ARMADO: hasta que el balon se juegue, el que saca no cruza el
     * medio campo y su primer golpeo sale hacia atras. Ver `Possession.kickoff`.
     */
    possession.kickoff = { takerId: taker.id, side: conceded };
    possession.lockout.clear();
    /*
     * Los rivales, fuera del circulo un instante.
     *
     * Es la otra mitad de la regla, y sin ella el saque de centro seria igual
     * de regalado que el de puerta: en 2v2 el unico rival de campo esta a dos
     * zancadas del punto central. `RESTART_LOCKOUT` es la misma proteccion que
     * ya usa el saque tras un fuera de juego.
     */
    for (const p of players) {
      if (p.side !== conceded && p.role !== "gk") {
        possession.lockout.set(p.id, OFFSIDE.RESTART_LOCKOUT);
      }
    }
    formUpForKickoff(taker);
    possession.pendingKickKind = null;
    possession.pendingKickSide = null;
    possession.crossFinish = null;
    possession.passSnapshot = null;
    possession.passSnapshotLoose = 0;
    lastHumanOwnerId = null;
    concedingSide = null;
    offsideSpot = null;
    offsideFreeze = null;
    offsideSnapshotPositions = null;
    offsideSnapshotBall = null;
    state.offsideTeam = null;
    state.offsideReview = null;
  };

  /**
   * Con el saque sin jugar, el que saca NO cruza el medio campo.
   *
   * Es la otra mitad de "hacia atras": sin esto, la regla del golpeo se esquiva
   * sin golpear — se conduce el balon en linea recta desde el punto central y
   * el saque de centro vuelve a ser una salida a la contra, que es justo lo que
   * se venia a quitar.
   *
   * Va justo DESPUES de mover a todo el mundo y ANTES de `updatePossession`:
   * ahi es donde el balon se pega al pie, asi que corregir al jugador aqui
   * arrastra el balon con el. Corregido despues, el balon se quedaria un tick
   * al otro lado de la linea.
   *
   * Se corre en cada tick porque es una pared, no un empujon: mantener el boton
   * de avanzar tiene que chocar contra ella, no ganarle por insistencia.
   */
  const holdKickoffLine = (): void => {
    const armed = possession.kickoff;
    if (!armed) return;

    // El balon ha dejado sus pies (se lo han quitado, o lo ha soltado): el
    // saque esta jugado y las dos restricciones se levantan.
    if (possession.ownerId !== armed.takerId) {
      possession.kickoff = null;
      return;
    }

    const taker = players.find((p) => p.id === armed.takerId);
    if (!taker) {
      possession.kickoff = null;
      return;
    }

    const limit = FIELD.WIDTH / 2 - (taker.radius + FIELD.LINE_WIDTH);
    if (armed.side === "home") {
      if (taker.pos.x > limit) {
        taker.pos.x = limit;
        if (taker.vel.x > 0) taker.vel.x = 0;
      }
    } else if (taker.pos.x < FIELD.WIDTH - limit) {
      taker.pos.x = FIELD.WIDTH - limit;
      if (taker.vel.x < 0) taker.vel.x = 0;
    }
  };

  /**
   * Cada equipo en SU campo, como en el futbol.
   *
   * `resetPlayers` devuelve a todo el mundo a su hueco de formacion, y esos
   * huecos no respetan el medio campo: medido sobre los spawns, el delantero
   * cruza la linea en 5v5 (864), 7v7 (896) y 11v11 (928 x2), y en 3v3 y 5v5 se
   * planta ademas DENTRO del circulo central. O sea que el saque empezaba con
   * un rival a dos zancadas del que saca — que es justo lo que la regla existe
   * para impedir.
   *
   * Dos correcciones, en este orden:
   *  1. Todo el mundo a su mitad. Con el radio y la raya descontados, para que
   *     nadie quede pisando la linea.
   *  2. Los rivales, ademas, fuera del circulo. Se empujan en linea recta desde
   *     el centro para no deshacer la forma del bloque: cada uno retrocede lo
   *     justo, nadie se recoloca.
   *
   * Al que saca no se le toca: ya esta puesto en el punto central.
   */
  const formUpForKickoff = (taker: Player): void => {
    const centreX = FIELD.WIDTH / 2;
    const centreY = FIELD.HEIGHT / 2;

    for (const p of players) {
      if (p.id === taker.id) continue;

      // 1. A su mitad.
      const edge = p.radius + FIELD.LINE_WIDTH;
      if (p.side === "home") p.pos.x = Math.min(p.pos.x, centreX - edge);
      else p.pos.x = Math.max(p.pos.x, centreX + edge);

      // 2. Los rivales del que saca, fuera del circulo.
      if (p.side !== taker.side) {
        const dx = p.pos.x - centreX;
        const dy = p.pos.y - centreY;
        const d = len2(dx, dy);
        const need = FIELD.CENTER_CIRCLE_RADIUS + p.radius;
        if (d < need) {
          // Justo encima del punto central no hay direccion que conservar: se
          // sale hacia su propia porteria, que es a donde tocaria retirarse.
          const ux = d > 1e-3 ? dx / d : (p.side === "home" ? -1 : 1);
          const uy = d > 1e-3 ? dy / d : 0;
          p.pos.x = centreX + ux * need;
          p.pos.y = centreY + uy * need;
          // Y si al salir del circulo se ha metido en el campo contrario, manda
          // la mitad: la regla del circulo no puede saltarse la del campo.
          if (p.side === "home") p.pos.x = Math.min(p.pos.x, centreX - edge);
          else p.pos.x = Math.max(p.pos.x, centreX + edge);
        }
      }

      confineToField(p);
      p.prevPos.x = p.pos.x;
      p.prevPos.y = p.pos.y;
      p.vel.x = 0;
      p.vel.y = 0;
    }
  };

  /**
   * Aparta a los sancionados del saque.
   *
   * Es la barrera del libre indirecto. Sin ella la sancion no sancionaba nada:
   * el balon aparecia a los pies de un defensa con el delantero que acababa de
   * estar en fuera de juego pegado a la espalda. Se empuja en linea recta desde
   * el balon —no se recoloca a nadie— para que el bloque siga teniendo la forma
   * que tenia, solo que sin nadie encima del que saca.
   */
  const clearRestartArea = (offendingSide: TeamSide, at: Vec2, exceptId: string | null): void => {
    for (const p of players) {
      if (p.side !== offendingSide) continue;
      if (p.id === exceptId) continue;
      const dx = p.pos.x - at.x;
      const dy = p.pos.y - at.y;
      const d = len2(dx, dy);
      if (d >= OFFSIDE.WALL_DISTANCE) continue;
      const to = wallRetreatPos(
        p.pos,
        at,
        OFFSIDE.WALL_DISTANCE,
        // Justo encima del balon no hay direccion que respetar: se retira hacia
        // su propia porteria, que es a donde retrocederia cualquiera.
        offendingSide === "home" ? -1 : 1,
      );
      p.pos.x = to.x;
      p.pos.y = to.y;
      confineToRestartField(p);
      p.prevPos.x = p.pos.x;
      p.prevPos.y = p.pos.y;
      p.vel.x = 0;
      p.vel.y = 0;
    }
  };

  const confineToRestartField = (p: Player): void => {
    p.pos.x = Math.max(p.radius, Math.min(FIELD.WIDTH - p.radius, p.pos.x));
    p.pos.y = Math.max(p.radius, Math.min(FIELD.HEIGHT - p.radius, p.pos.y));
  };

  /** Saque indirecto para el equipo que se beneficio del fuera de juego. */
  const restartFromOffside = (offendingSide: TeamSide, spot: Vec2): void => {
    const defendingSide: TeamSide = offendingSide === "home" ? "away" : "home";
    const dir = defendingSide === "home" ? 1 : -1;
    const at = offsideRestartSpot(defendingSide, spot);

    ball.pos.x = at.x;
    ball.pos.y = at.y;
    ball.prevPos.x = ball.pos.x;
    ball.prevPos.y = ball.pos.y;
    ball.vel.x = 0;
    ball.vel.y = 0;
    ball.spin = 0;
    ball.topSpin = 0;
    ball.loft = 0;
    ball.prevLoft = 0;
    ball.loftVel = 0;
    ball.unstoppable = false;

    const takers = players.filter((p) => p.side === defendingSide && p.role !== "gk");
    let taker = takers[0] ?? null;
    let bestDist = Infinity;
    for (const p of takers) {
      const d = len2(p.pos.x - at.x, p.pos.y - at.y);
      if (d < bestDist) {
        taker = p;
        bestDist = d;
      }
    }

    if (taker) {
      // El que saca se coloca DETRAS del balon, mirando hacia adelante: el
      // balon queda entre el y el campo por jugar, como en cualquier saque.
      const offset = taker.radius + ball.radius + 2;
      taker.pos.x = at.x - dir * offset;
      taker.pos.y = at.y;
      confineToRestartField(taker);
      taker.prevPos.x = taker.pos.x;
      taker.prevPos.y = taker.pos.y;
      taker.vel.x = 0;
      taker.vel.y = 0;
      taker.facing.x = dir;
      taker.facing.y = 0;
      taker.aimDir.x = dir;
      taker.aimDir.y = 0;
      taker.chargeSteer = false;
      taker.aimSweep = 0;
      taker.charge = 0;
      possession.ownerId = taker.id;
    } else {
      possession.ownerId = null;
    }

    clearRestartArea(offendingSide, at, null);

    possession.lockout.clear();
    // Y ademas nadie del equipo sancionado puede robar en el mismo instante:
    // retirarse a la barrera no sirve de nada si se puede volver corriendo
    // antes de que al que saca le de tiempo a tocar el balon.
    for (const p of players) {
      if (p.side === offendingSide) possession.lockout.set(p.id, OFFSIDE.RESTART_LOCKOUT);
    }
    possession.pendingKickKind = null;
    possession.pendingKickSide = null;
    possession.crossFinish = null;
    possession.passSnapshot = null;
    possession.passSnapshotLoose = 0;
    lastHumanOwnerId = null;
    chargingId = null;
    offsideSpot = null;
    offsideFreeze = null;
    offsideSnapshotPositions = null;
    offsideSnapshotBall = null;
    state.offsideTeam = null;
    state.offsideReview = null;
  };

  /**
   * El congelado del VAR: todo el mundo al instante EXACTO del pase.
   *
   * Es la imagen que decide, y tiene que salir de las mismas posiciones con las
   * que se juzgo la jugada (`PassSnapshot`), no de una reconstruccion: si la
   * linea se dibujara sobre otras posiciones, la repeticion estaria enseñando
   * una jugada distinta de la que se ha sancionado.
   */
  const applyOffsideFreeze = (): void => {
    if (!offsideSnapshotPositions) return;
    for (const p of players) {
      const at = offsideSnapshotPositions.get(p.id);
      if (!at) continue;
      p.prevPos.x = p.pos.x;
      p.prevPos.y = p.pos.y;
      p.pos.x = at.x;
      p.pos.y = at.y;
      p.vel.x = 0;
      p.vel.y = 0;
    }
    if (offsideSnapshotBall) {
      ball.prevPos.x = ball.pos.x;
      ball.prevPos.y = ball.pos.y;
      ball.pos.x = offsideSnapshotBall.x;
      ball.pos.y = offsideSnapshotBall.y;
      ball.vel.x = 0;
      ball.vel.y = 0;
      ball.loft = 0;
      ball.prevLoft = 0;
      ball.loftVel = 0;
    }
  };

  /**
   * El balon acaba de entrar y se queda dentro de la porteria.
   *
   * Antes se le ponia la velocidad a cero en el mismo instante del gol: el
   * balon se paraba clavado en la linea mientras la red se movia sola, que es
   * exactamente al reves de lo que pasa. Ahora sigue su camino y es la red la
   * que lo para — se come casi toda la velocidad y lo deja muerto dentro.
   *
   * Hace falta contener el balon a mano porque detras de la linea de gol no
   * hay pared: durante el partido eso no importa, porque el gol para el juego,
   * pero si le dejamos correr habria que verlo alejarse hasta el infinito.
   */
  /**
   * Cuanto ha cedido la red bajo el balon, y a que velocidad.
   *
   * Vive en la SIMULACION, no en el render, porque mueve el balon: un paño que
   * cede a los fps de cada maquina desincronizaria el partido. Aqui va a dt
   * fijo y todo el mundo ve el mismo bolsillo.
   */
  let netGive = 0;
  let netGiveVel = 0;
  /**
   * El paño tiene el balon cogido.
   *
   * Sin esto el balon rebotaba en el primer contacto y el bolsillo se hundia
   * SIN el dentro: la red se iba por un lado y el balon se quedaba delante,
   * que es exactamente lo contrario de lo que se venia a hacer. Mientras esta
   * cogido, el balon va montado en el paño — entra con el y sale con el.
   */
  let netHolding = false;

  /** Muelle del paño visto desde la simulacion. Blando: es tela, no pared. */
  const NET_K = 46;
  const NET_C = 7.5;
  /**
   * Que fraccion del impulso del balon se queda la red al frenarlo.
   *
   * Pequeña a proposito. Con 0,55 cualquier disparo por encima de 1600 u/s
   * saturaba el tope y todos los goles hundian la red exactamente igual: la
   * diferencia entre un trallazo y un gol a puerta vacia desaparecia, que era
   * justo lo que se queria enseñar.
   */
  const NET_TRANSFER = 0.16;
  /**
   * Cuanto puede ceder la red como maximo.
   *
   * El plano de reposo del balon se adelanta esta misma cantidad, asi que con
   * cesion maxima el balon acaba justo en el fondo del marco y nunca asoma por
   * detras. En la practica: un gol flojo se queda a media porteria y un
   * trallazo hunde el paño hasta el fondo. La diferencia se ve.
   */
  const NET_MAX_GIVE = FIELD.GOAL_DEPTH * 0.5;

  const settleInsideGoal = (dt: number): void => {
    const mouth = goalMouth();
    const r = ball.radius;
    const past = ball.pos.x < 0 ? -1 : ball.pos.x > FIELD.WIDTH ? 1 : 0;

    // El paño siempre tiende a volver, tenga balon encima o no.
    netGiveVel += (-NET_K * netGive - NET_C * netGiveVel) * dt;
    netGive = Math.min(NET_MAX_GIVE, Math.max(0, netGive + netGiveVel * dt));

    if (past === 0) return;

    const backEdge = FIELD.GOAL_DEPTH - r - NET_MAX_GIVE;
    const nominalBack = past < 0 ? -backEdge : FIELD.WIDTH + backEdge;
    // El fondo no esta fijo: es una tela que ya ha cedido `netGive`.
    const backX = nominalBack + past * netGive;
    const reaching = past < 0 ? ball.pos.x <= backX : ball.pos.x >= backX;
    if (reaching && !netHolding) {
      // Lo que el balon traia se lo queda la red: por eso un trallazo hunde el
      // paño mucho mas hondo que un gol a puerta vacia.
      netGiveVel += Math.abs(ball.vel.x) * NET_TRANSFER;
      netHolding = true;
    }

    if (netHolding) {
      // Montado en el paño: entra con el hasta el fondo del bolsillo y vuelve
      // con el cuando la red le devuelve. Una red no rebota, atrapa.
      ball.pos.x = backX;
      ball.vel.x = past * netGiveVel;
      ball.vel.y *= 0.82;
      ball.spin *= 0.8;
      if (netGive <= 0.02 && Math.abs(netGiveVel) < 1) {
        netHolding = false;
        ball.vel.x = 0;
      }
    }

    // Los laterales de la red, igual de blandos.
    if (ball.pos.y < mouth.top + r) {
      ball.pos.y = mouth.top + r;
      ball.vel.y = Math.abs(ball.vel.y) * 0.08;
    } else if (ball.pos.y > mouth.bottom - r) {
      ball.pos.y = mouth.bottom - r;
      ball.vel.y = -Math.abs(ball.vel.y) * 0.08;
    }

    // Y una vez dentro no vuelve a salir por la boca. Un balon paseandose de
    // vuelta al campo mientras se celebra el gol se lee como que el gol no ha
    // subido; ademas es justo donde apunta la camara y donde se mueve la red.
    const lineX = past < 0 ? -r * 0.5 : FIELD.WIDTH + r * 0.5;
    const escaping = past < 0 ? ball.pos.x > lineX : ball.pos.x < lineX;
    if (escaping) {
      ball.pos.x = lineX;
      ball.vel.x = 0;
    }
  };

  /**
   * Los segundos entre marcar y la repeticion.
   *
   * Antes se congelaba todo de golpe: balon parado en seco, jugadores como
   * estatuas y corte a repeticion. Marcar es lo unico que un juego de futbol
   * existe para producir, y se despachaba en un fotograma. Aqui el balon
   * termina su viaje dentro de la red y TU SIGUES PILOTANDO: puedes salir
   * corriendo a celebrarlo, que es lo que hace cualquiera.
   *
   * Lo que NO corre: la IA no decide (el equipo se queda coasteando hasta
   * pararse, como cuando cae un gol), no hay posesion, no hay golpeos y no se
   * graba nada — el buffer de repeticion ya tiene el gol dentro y grabar la
   * celebracion encima lo borraria.
   */
  const celebrate = (
    dt: number,
    frame: InputFrame | null,
    external: ReadonlyMap<string, InputFrame>,
    multiAgent: boolean,
  ): void => {
    stepBall(ball, dt);
    // Arrastre de la malla: dentro de la porteria el balon muere en un par de
    // toques en vez de rodar por detras como si fuera cesped.
    if (ball.pos.x < 0 || ball.pos.x > FIELD.WIDTH) {
      const drag = dexp(-6 * dt);
      ball.vel.x *= drag;
      ball.vel.y *= drag;
    }
    settleInsideGoal(dt);

    const ownFrame = (player: Player): InputFrame | null =>
      external.get(player.id) ?? (!multiAgent && player.id === controlledId ? frame : null);
    trackKicks(ownFrame, dt);
    for (const player of players) {
      stepPlayer(player, ownFrame(player), dt);
    }
    resolvePlayerCollisions(players);
  };

  const tickCore = (
    dt: number,
    frame: InputFrame | null,
    externalFrames: ReadonlyMap<string, InputFrame> | null,
  ): void => {
    showingReplay = false;
    // Copia ordenada: el resultado no depende del orden de insercion del Map.
    const external = new Map<string, InputFrame>();
    if (externalFrames) {
      for (const [id, externalFrame] of [...externalFrames].sort(([a], [b]) => a.localeCompare(b))) {
        const player = players.find((p) => p.id === id);
        if (player?.role !== "gk") external.set(id, externalFrame);
      }
    }
    const multiAgent = external.size > 1;
    controlledId = externalFrames === null ? humanId : external.size === 1 ? [...external.keys()][0]! : null;
    const effectiveFrame =
      frame ?? (!multiAgent && controlledId !== null ? (external.get(controlledId) ?? null) : null);

    /*
     * Quien es humano, tambien desde los inputs. `human` pesa en la IA (los
     * companeros se la pasan antes a una persona), y en red NO puede salir de
     * los asientos con que se creo la simulacion: el que entra a mitad se crea
     * sabiendose humano desde el tick 0, mientras que en la pantalla del
     * anfitrion ese asiento fue un bot hasta que llego. Mismos inputs, pases
     * distintos. Con `autopilot` no se toca: ahi no hay personas.
     */
    if (externalFrames !== null && !autopilot) {
      for (const p of players) {
        if (p.role !== "gk") p.human = external.has(p.id);
      }
    }

    /*
     * DESCANSO: todo el mundo quieto.
     *
     * Se corta antes de mover a nadie, igual que la cuenta atras del saque. El
     * saque ya esta colocado (lo hace `beginBreak`), asi que estos segundos son
     * solo para ver el marcador y respirar. Sin este corte el balon seguia vivo
     * en el centro y lo empujaba el primero que llegase.
     */
    if (state.breakPause > 0) {
      tickClock(dt);
      return;
    }

    if (state.phase === "playing") {
      const tacticalId = multiAgent ? null : tacticalHumanId();
      aiWorld.humanId = tacticalId;
      updateTactics(players, ball, possession, tacticalId, dt, state);

      if (possession.passSnapshot && !possession.ownerId) {
        possession.passSnapshotLoose += dt;
        if (possession.passSnapshotLoose > OFFSIDE.PASS_WINDOW) {
          possession.passSnapshot = null;
          possession.passSnapshotLoose = 0;
        }
      } else if (possession.ownerId) {
        possession.passSnapshotLoose = 0;
      }
      // Todos deciden antes de mover. Los externos sustituyen a su cerebro,
      // salvo con `keepAiAwake`, que lo deja decidir igualmente para poder
      // consultarlo despues via `aiFrameFor`.
      for (const brain of brains) {
        if (!keepAiAwake && external.has(brain.playerId)) continue;
        if (brain.playerId === controlledId && effectiveFrame && !multiAgent) continue;
        aiFrames.set(brain.playerId, brain.update(aiWorld, dt));
      }
    }

    if (state.phase === "goal") {
      state.goalPause -= dt;
      /*
       * Tres tramos, en el orden en que se ven: celebracion, repeticion y
       * cuenta atras. `goalPause` cuenta hacia atras, asi que la lectura va al
       * reves: queda mucho en la celebracion y poco en la cuenta.
       *
       * La cuenta va la ULTIMA a proposito. Antes se pasaba de la repeticion al
       * juego en un fotograma: el clip terminaba y el balon ya estaba rodando,
       * con lo que el primer segundo del saque se jugaba sin que nadie se
       * hubiera enterado de que habia empezado. Ahora el saque se COLOCA al
       * entrar en la cuenta, todo el mundo lo ve quieto tres segundos, y solo
       * despues corre el reloj.
       */
      const afterReplay = state.goalPause - MATCH.RESTART_COUNTDOWN;
      const inCountdown = afterReplay <= 0;

      if (inCountdown) {
        // Colocar el saque UNA sola vez, no en cada tick de la cuenta: si no,
        // `resetPlayers` estaria devolviendo a todo el mundo al spawn 60 veces
        // por segundo y la cuenta no seria una pausa, seria una paralisis.
        if (!restartPlaced) {
          restartPlaced = true;
          // Muerte subita, o el tiempo se agoto durante la celebracion: el gol
          // que se acaba de ver es el ultimo, y no hay saque que contar.
          if (state.goldenGoal || (state.timeRemaining <= 0 && timeUpEndsMatch())) {
            state.phase = "ended";
            return;
          }
          /*
           * Fin de un tiempo que NO es el ultimo: se descansa en vez de sacar.
           * `tickClock` se encarga del saque cuando acabe el descanso.
           */
          if (state.timeRemaining <= 0 && state.period < state.periods) {
            beginBreak();
            state.phase = "playing";
            return;
          }
          kickoffFromCentre(concedingSide ?? "home");
        }
        // Sin nadie mirando (calibracion y tests) no hay nada que contar: se
        // saca ya. Esperar 3 s por gol solo alarga los scripts.
        if (autopilot || state.goalPause <= 0) {
          state.goalPause = 0;
          state.phase = "playing";
        }
        return;
      }

      if (!autopilot && afterReplay <= MATCH.REPLAY_PLAYBACK) {
        const replayElapsed = MATCH.REPLAY_PLAYBACK - afterReplay;
        /*
         * El final del clip va a camara lenta.
         *
         * Con avance lineal, el balon entraba justo en el ultimo fotograma y
         * la red se quedaba sin verse. Estirando solo la cola —el tramo en el
         * que el balon ya esta dentro— la jugada llega a su ritmo y el gol se
         * queda en pantalla el rato que merece.
         */
        const t = replayElapsed / MATCH.REPLAY_PLAYBACK;
        const tail = MATCH.REPLAY_SLOWMO_TAIL;
        showingReplay = true;
        if (t < 1 - tail || !replay.hasTail()) {
          const eased = t < 1 - tail ? t / (1 - tail) : 1;
          replay.applyAt(eased * MATCH.REPLAY_CLIP, players, ball, possession);
        } else {
          // La cola: lo que paso DESPUES de cruzar la linea. Sin ella, este
          // tramo era el ultimo fotograma repetido 1,77 s — la repeticion se
          // paraba justo en lo mejor.
          replay.applyTail((t - (1 - tail)) / tail, players, ball, possession);
        }
      } else {
        celebrate(dt, frame, external, multiAgent);
        // Se graba MIENTRAS se celebra, que es lo que llena la cola de la
        // repeticion de despues. La celebracion va antes que el clip, asi que
        // para cuando toca reproducir ya esta entera.
        if (!autopilot) replay.recordTail(players, ball, possession, kicks);
      }
      return;
    }

    if (state.phase === "offside") {
      state.goalPause -= dt;
      /*
       * La revision, en el orden de una retransmision: aviso en vivo,
       * repeticion y congelado con la linea. El congelado va el ULTIMO porque
       * es la imagen que decide, y la que tiene que quedarse en la retina justo
       * antes de reanudar.
       *
       * `autopilot` (calibracion y tests sin render) se salta el espectaculo:
       * ahi no hay nadie mirando y mover a los jugadores solo alarga el reloj.
       */
      if (!autopilot) {
        const stage = varStage(state);
        if (stage === "replay") {
          showingReplay = true;
          replay.applyAt(varReplayProgress(state) * MATCH.REPLAY_CLIP, players, ball, possession);
        } else if (stage === "freeze") {
          applyOffsideFreeze();
        }
      }
      if (state.goalPause <= 0 && offsideSpot && state.offsideTeam) {
        // Antes de sacar, todo el mundo vuelve a donde estaba cuando se pito:
        // la revision ha estado moviendolos por media jugada.
        if (offsideFreeze) restoreSnapshot(offsideFreeze, players, ball, possession);
        restartFromOffside(state.offsideTeam, offsideSpot);
        state.phase = "playing";
      }
      return;
    }

    if (state.phase === "ended") return;

    const controlled = human();
    const owner = findOwner(players, possession);

    if (effectiveFrame && !multiAgent && humanCharges) {
      if (owner === controlled) beginChargeIfPressed(controlled, effectiveFrame);
      else if (chargingId !== null) cancelCharge(controlled);
      else controlled.chargeSteer = false;
    } else if (multiAgent && humanCharges) {
      /*
       * En red carga CUALQUIER asiento humano, no solo el tuyo. Aqui vivia un
       * `owner.id === humanId`, y `humanId` es distinto en cada cliente: tu
       * golpeo se cargaba en tu pantalla y en la del otro no salia nunca.
       */
      if (chargingId !== null && chargingId !== owner?.id) {
        const stale = players.find((p) => p.id === chargingId);
        if (stale) cancelCharge(stale);
        else chargingId = null;
      }
      if (owner && external.has(owner.id)) beginChargeIfPressed(owner, external.get(owner.id)!);
    }

    const frameFor = (player: Player): InputFrame | null => {
      const externalFrame = external.get(player.id);
      if (externalFrame) return externalFrame;
      if (!multiAgent && player.id === controlledId && effectiveFrame) return effectiveFrame;
      return aiFrames.get(player.id) ?? null;
    };
    if (!autopilot) trackKicks(frameFor, dt);

    // Caño con O si hay rival en cono. Humano siempre; bots solo en lados chicos
    // (si no, el dash de escape en 7v7/11v11 se convierte en caño y muere el remate).
    const preOwnerFrame = owner ? frameFor(owner) : null;
    if (owner && preOwnerFrame?.pressed.has("dash") === true && owner.trapTime <= 0) {
      const ownerIsHuman = !multiAgent && owner.id === controlledId;
      /*
       * PLANTAR EL CUERPO. La misma O, protegiendo el balon.
       *
       * Se arma aqui —antes del bucle que resuelve los cuerpos de los
       * defensores— para que pulsar "a la vez" funcione de verdad: dentro de un
       * mismo tick, el que lleva el balon siempre se cubre antes de que le
       * embistan. Si se armara en el bucle, quien ganase dependeria del orden
       * del array de jugadores, que es la peor manera de decidir un duelo.
       *
       * El enfriamiento del dash es la unica tarifa, y ya la cobra `stepPlayer`
       * un poco mas abajo por el regate que sale de esta misma pulsacion. Por
       * eso se exige que este a cero: si no, aporrear la O saldria gratis y el
       * cuerpo dejaria de existir como recurso.
       *
       * Cargando un golpeo no se puede: ahi `stepPlayer` no dashea, o sea que
       * no habria enfriamiento que pagar y la cobertura seria infinita.
       */
      if (owner.dashCooldown <= 0 && !owner.chargeSteer) {
        owner.braceTime = BODY_CHECK.BRACE_WINDOW;
      }
      const opponents = players.filter((p) => p.side !== owner.side);
      if (ownerIsHuman || fieldPlayersPerTeam() <= 3) {
        const rearNutmeg = applyBackNutmeg(ball, owner, possession, opponents, preOwnerFrame.move);
        if (rearNutmeg.applied) {
          if (rearNutmeg.victim) hooks.onNutmeg?.(owner, rearNutmeg.victim);
          if (ownerIsHuman) cancelCharge(owner);
        } else {
          const nutmegeVictim = applyNutmeg(ball, owner, possession, opponents, preOwnerFrame.move);
          if (nutmegeVictim) {
            hooks.onNutmeg?.(owner, nutmegeVictim);
            if (ownerIsHuman) cancelCharge(owner);
          }
        }
      }
    }

    /**
     * Golpeo de primera. Devuelve true si se ha golpeado el balon.
     *
     * No exige tener la posesion — ese es justo el punto: el balon viene
     * llegando y se golpea al vuelo. Se bloquea la recaptura del rematador
     * para que no se lo devuelva a si mismo en el tick siguiente.
     */
    /** Ejecuta el golpeo de un gesto ya armado. */
    const strikeFirstTime = (player: Player, swing: FirstTimeSwing): void => {
      const opponents = players.filter((p) => p.side !== player.side);
      kickBall(
        ball,
        player,
        possession,
        {
          kind: swing.kind,
          dir: swing.dir,
          power: firstTimePower(swing.quality),
          aiming: false,
          // La direccion NO se bloquea: la dispersion es la que traduce el
          // timing en precision, y es de donde sale toda la tension.
          assistSpreadScale: firstTimeSpreadScale(swing.quality) * assist.spreadScale,
          opponents,
          shotStyle:
            swing.kind === "shoot"
              ? firstTimeShotStyle({ timeToContact: 0, quality: swing.quality, airborne: swing.airborne })
              : undefined,
          preserveBallPosition: true,
        },
        players,
      );

      possession.ownerId = null;
      possession.lockout.set(player.id, FIRST_TIME.RECAPTURE_LOCKOUT);

      hooks.onFirstTime?.(gradeOf(swing.quality), player, swing.kind, swing.quality);
      hooks.onKick?.(swing.kind, player, swing.quality);
    };

    /**
     * ARMA el remate de primera. Devuelve true si el gesto ha empezado.
     *
     * No golpea: eso pasa cuando el balon llega de verdad
     * (`resolveFirstTimeSwings`). La ventana permite pulsar con el balon a
     * 0,3 s, que a 600 u/s son 213 unidades — golpear ahi es lo que hacia que
     * el jugador le pegara al aire mientras el balon cambiaba de direccion solo.
     *
     * No exige la posesion — ese es justo el punto: el balon viene llegando y
     * se golpea al vuelo. Se bloquea la recaptura del rematador para que no se
     * lo devuelva a si mismo en el tick siguiente.
     */
    const tryFirstTime = (player: Player, frame: InputFrame): boolean => {
      /*
       * Un solo boton tambien aqui. Antes se armaba de tiro o de pase segun la
       * tecla; ahora se arma el gesto y punto, y el tipo lo decide la carga al
       * soltar, igual que un golpeo normal.
       */
      const wantsShot =
        frame.pressed.has("kick") || frame.pressed.has("shoot") || frame.pressed.has("pass");
      if (!wantsShot) return false;
      if (possession.ownerId === player.id) return false;
      if (player.trapTime > 0) return false;
      if (player.firstTimeSwing) return false; // ya vas armado: no se re-arma

      const window = findFirstTimeWindow(ball, player, possession);
      if (!window) return false;

      const dir = frame.move.x !== 0 || frame.move.y !== 0 ? frame.move : player.facing;
      player.firstTimeSwing = {
        time: FIRST_TIME.SWING_TIME,
        kind: wantsShot ? "shoot" : "pass",
        // Copia: `frame.move` es del input y puede cambiar bajo los pies.
        dir: { x: dir.x, y: dir.y },
        quality: window.quality,
        airborne: window.airborne,
      };
      return true;
    };

    /**
     * Resuelve los gestos armados: golpea si el balon ha llegado, y si se
     * acaba el tiempo, se pega al aire.
     *
     * Va ANTES de `updatePossession` a proposito: si corriera despues, el
     * jugador habria controlado el balon y el remate no saldria nunca. Haber
     * pulsado significa que eliges rematar en vez de controlar.
     */
    const resolveFirstTimeSwings = (dt: number): void => {
      for (const player of players) {
        const swing = player.firstTimeSwing;
        if (!swing) continue;

        const reach = player.radius + ball.radius + FIRST_TIME.CONTACT_PAD;
        // Contra el SEGMENTO que recorrio el balon, no contra su posicion: un
        // pase fuerte avanza 13 u por tick y uno muy fuerte 40, asi que podria
        // atravesar la zona de contacto entre dos ticks y fallar un remate que
        // el jugador vio pasar por sus pies.
        const contact = closestPointOnSegment(player.pos, ball.prevPos, ball.pos);
        if (len2(contact.x - player.pos.x, contact.y - player.pos.y) <= reach) {
          player.firstTimeSwing = null;
          // Se retrocede el balon al punto de contacto de verdad. Golpearlo
          // desde el final del recorrido del tick lo dejaba a 44 unidades del
          // jugador en la mediana, con el contacto en 17: seguia leyendose como
          // pegarle a distancia.
          ball.pos.x = contact.x;
          ball.pos.y = contact.y;
          strikeFirstTime(player, swing);
          continue;
        }

        swing.time -= dt;
        if (swing.time <= 0) {
          player.firstTimeSwing = null;
          hooks.onFirstTimeWhiff?.(player);
        }
      }
    };

    for (const player of players) {
      const playerFrame = frameFor(player);
      const externallyControlled = external.has(player.id);
      const legacyHuman = !multiAgent && effectiveFrame !== null && player.id === controlledId;

      // De PRIMERA antes que entrada: si el balon viene hacia ti, la tecla de
      // disparo tiene que golpearlo, no barrer. Solo si no hay ventana abierta
      // se interpreta como entrada.
      //
      // Abierto a TODOS, no solo a humanos: la IA tambien remata de primera
      // (decide en brain.ts, con su propio error de timing). Si fuera exclusivo
      // del humano seria una herramienta que el rival no tiene, y el gesto mas
      // vistoso del juego no se veria nunca en un partido contra la maquina.
      const struckFirstTime = playerFrame ? tryFirstTime(player, playerFrame) : false;

      /*
       * Poner el cuerpo: la O sin balon. Va ANTES de `stepPlayer` para que la
       * embestida cuente en el movimiento de este mismo tick.
       *
       * El caño (la O CON balon) se resuelve mas arriba, asi que la tecla tiene
       * un solo significado en cada situacion: con balon te quitas a alguien de
       * encima, sin balon se lo pones tu.
       *
       * Y si aqui NO hay a quien embestir, `applyBodyCheck` no gasta
       * enfriamiento y `stepPlayer` convierte la misma pulsacion en carrera.
       * Ese es el reparto entero: cerca del rival, cuerpo; en campo abierto,
       * dash. Sin el dash, el que defiende no tenia con que perseguir a un
       * atacante que si podia cambiar de ritmo.
       */
      if (
        !struckFirstTime &&
        player !== owner &&
        playerFrame?.pressed.has("dash") === true
      ) {
        const shoved = applyBodyCheck(
          player,
          players.filter((p) => p.side !== player.side),
          playerFrame.move,
          ball,
          possession.ownerId,
        );
        if (shoved?.blocked) hooks.onBodyBlock?.(player, shoved.victim);
        else if (shoved) hooks.onBodyCheck?.(player, shoved.victim);
      }

      const tackleBall =
        !struckFirstTime &&
        (externallyControlled || legacyHuman) &&
        owner?.id !== player.id &&
        (playerFrame?.pressed.has("kick") === true ||
          playerFrame?.pressed.has("shoot") === true) &&
        chargingId !== player.id
          ? ball.pos
          : null;

      // Estirada del portero. Va ANTES de `stepPlayer` porque lo que hace es
      // fijarle la velocidad, igual que el dash: si se decidiera despues, el
      // salto no empezaria hasta el tick siguiente y a 60 Hz eso son 12 u de
      // balon perdidas justo cuando mas se notan.
      if (player.role === "gk") {
        // El orden importa: la estirada tiene preferencia. A un balon raso se
        // va con el brazo, no saltando; el salto es solo para lo que se le va
        // por arriba, y probarlo primero le haria saltar a disparos rasos.
        if (tryGoalkeeperDive(ball, player, random)) hooks.onDive?.(player);
        else if (tryGoalkeeperJump(ball, player, random)) hooks.onDive?.(player);
      }

      const { dashed, tackled } = stepPlayer(
        player,
        playerFrame,
        dt,
        1,
        tackleBall,
        player === owner,
      );
      if (legacyHuman) {
        if (dashed) hooks.onDash?.(player.pos.x, player.pos.y, player.facing.x, player.facing.y, player.side);
        if (tackled) hooks.onTackle?.(player.pos.x, player.pos.y);
      }
    }

    holdKickoffLine();

    const ownerFrame = owner ? frameFor(owner) : null;
    const ownerIsLegacyHuman =
      !multiAgent && effectiveFrame !== null && owner?.id === controlledId && humanCharges;

    if (owner && ownerIsLegacyHuman) {
      chargeAndKick(controlled, effectiveFrame!, dt);
    } else if (
      owner &&
      ownerFrame &&
      (owner.trapTime <= 0 || (allowsTrapShoot(possession, owner) && ownerFrame.pressed.has("shoot")))
    ) {
      if (multiAgent && owner.id === chargingId && humanCharges) {
        chargeAndKick(owner, ownerFrame, dt);
      } else {
      const dir =
        ownerFrame.move.x !== 0 || ownerFrame.move.y !== 0 ? ownerFrame.move : owner.facing;
      const power = ownerFrame.kickPower ?? 1;
      const opponents = players.filter((p) => p.side !== owner.side);

      if (ownerFrame.pressed.has("fake")) {
        const allowCut = (!multiAgent && owner.id === controlledId) || fieldPlayersPerTeam() <= 3;
        if (allowCut && applyCut(ball, owner, possession, opponents, ownerFrame.move)) {
          hooks.onCut?.(owner);
        } else {
          applyFake(owner, opponents);
        }
      } else if (ownerFrame.pressed.has("shoot")) {
        kickBall(ball, owner, possession, {
          kind: "shoot",
          dir,
          power,
          aiming: false,
          directionLocked: true,
          opponents,
          shotStyle: ownerFrame.shotStyle,
        }, players);
        hooks.onKick?.("shoot", owner, power);
      } else if (ownerFrame.pressed.has("pass") && owner.trapTime <= 0) {
        const kind = ownerFrame.kickKind === "cross" || shouldCross(owner, dir) ? "cross" : "pass";
        kickBall(ball, owner, possession, {
          kind,
          dir,
          power,
          aiming: false,
          directionLocked: true,
          opponents,
        }, players);
        hooks.onKick?.(kind, owner, power);
      }
      }
    }

    if (!multiAgent && effectiveFrame && owner?.id !== controlledId && chargingId !== null) {
      cancelCharge(controlled);
    }

    // "Mini pase": soltar Shift mientras se sigue siendo el dueno empuja el
    // balon hacia delante. Se comprueba aqui, tras el golpeo (que ya habria
    // puesto a `null` el dueno si el jugador ha chutado/pasado este tick), asi
    // que un golpeo real nunca se confunde con soltar el control cerrado.
    const ownerBeforeUpdate = findOwner(players, possession);
/*
     * Aqui vivia el "mini pase" al soltar Shift: al dejar de proteger el balon
     * se le daba un empujoncito. Sin control cerrado no hay nada que soltar.
     */

    resolveFirstTimeSwings(dt);

    updatePossession(ball, players, possession, dt, (kind, from, by) => {
      hooks.onPossessionLost?.(kind, from, by);
    });

    const newOwner = findOwner(players, possession);
    const snapshot = possession.passSnapshot;
    if (newOwner && snapshot && !ownerBeforeUpdate) {
      const violation = checkOffsideOnReceive(newOwner, snapshot, players);
      if (violation) {
        possession.ownerId = null;
        ball.vel.x = 0;
        ball.vel.y = 0;
        ball.loftVel = 0;
        // Se guarda el campo tal cual esta AHORA, antes de que la revision
        // empiece a moverlo, y tambien el instante del pase para el congelado.
        offsideFreeze = captureSnapshot(players, ball, possession);
        offsideSnapshotPositions = snapshot.positions;
        offsideSnapshotBall = { x: snapshot.ballX, y: snapshot.ballY };
        possession.passSnapshot = null;
        possession.passSnapshotLoose = 0;
        offsideSpot = violation.spot;
        const defendingSide: TeamSide =
          snapshot.attackingSide === "home" ? "away" : "home";
        state.offsideTeam = snapshot.attackingSide;
        state.offsideReview = {
          team: snapshot.attackingSide,
          playerId: newOwner.id,
          lineX: violation.lineX,
          attacker: { x: violation.spot.x, y: violation.spot.y },
          defender: { x: violation.defender.x, y: violation.defender.y },
          lineFrom: violation.lineFrom,
          ball: { x: violation.ball.x, y: violation.ball.y },
          spot: offsideRestartSpot(defendingSide, violation.spot),
        };
        state.phase = "offside";
        state.goalPause = OFFSIDE.PAUSE;
        hooks.onOffside?.(snapshot.attackingSide);
        tickClock(dt);
        return;
      }
      if (newOwner.side === snapshot.attackingSide && newOwner.id !== snapshot.passerId) {
        possession.passSnapshot = null;
        possession.passSnapshotLoose = 0;
      }
    } else if (newOwner && snapshot && newOwner.side !== snapshot.attackingSide) {
      possession.passSnapshot = null;
      possession.passSnapshotLoose = 0;
    }

    if (!multiAgent && controlledId !== null && newOwner?.id === controlledId) {
      if (lastHumanOwnerId !== controlledId && newOwner.trapTime > 0) forgiveTrap(newOwner, assist);
      lastHumanOwnerId = controlledId;
    } else {
      lastHumanOwnerId = null;
    }

    resolvePlayerCollisions(players).forEach((bump) => {
      if (bump.sideline) hooks.onSidelineBump?.(bump.x, bump.y, bump.intensity);
    });
    applyAftertouch(dt, frameFor);
    stepBall(ball, dt);
    collideBallWithPlayers(ball, players, possession.ownerId);
    const { goal, postHit } = collideBallWithWalls(ball);
    if (postHit) hooks.onPostHit?.(postHit.x, postHit.y, postHit.speed);

    if (goal) {
      state.score[goal.scorer] += 1;
      state.lastScorer = goal.scorer;
      concedingSide = goal.conceded;
      // El balon NO se para: entra, mueve la red y muere dentro. Ver `celebrate`.
      possession.ownerId = null;
      if (!autopilot) {
        replay.record(players, ball, possession, kicks);
        // A partir de aqui se graba la celebracion, para que la repeticion no
        // se quede congelada en el fotograma del gol. Ver `ReplayBuffer.tail`.
        replay.beginTail();
      }
      state.phase = "goal";
      state.goalPause =
        MATCH.GOAL_CELEBRATION + MATCH.REPLAY_PLAYBACK + MATCH.RESTART_COUNTDOWN;
      restartPlaced = false;
      hooks.onGoal?.(goal.scorer);
      hooks.onGoalNet?.(goal.conceded, goal.y, goal.speed);
    } else if (!autopilot) {
      replay.record(players, ball, possession, kicks);
    }

    tickClock(dt);
  };

  return {
    skipGoalPause(): void {
      if (state.phase !== "goal" && state.phase !== "offside") return;
      // Se corta hasta el ultimo tramo, no hasta cero: la cuenta atras es lo
      // que da tiempo a colocarse.
      if (state.goalPause > MATCH.RESTART_COUNTDOWN) {
        state.goalPause = MATCH.RESTART_COUNTDOWN;
      }
    },
    players,
    ball,
    possession,
    state,
    brains,
    aiWorld,
    assist,
    get humanId(): string {
      return humanId;
    },
    /**
     * Cuanto ha cedido la red bajo el balon. Lo lee el render para dibujar el
     * bolsillo exactamente donde la fisica lo puso: recalcularlo por fuera es
     * como el dibujo y la fisica acaban contando cosas distintas.
     */
    get netGive(): number {
      return netGive;
    },
    aiFrameFor(id: string): InputFrame | null {
      return aiFrames.get(id) ?? null;
    },
    replayLook(): Float32Array | null {
      return showingReplay ? replay.shownLook() : null;
    },
    firstTimeWindow(playerId?: string) {
      const player = players.find((p) => p.id === (playerId ?? humanId));
      if (!player || state.phase !== "playing") return null;
      const window = findFirstTimeWindow(ball, player, possession);
      return window ? { quality: window.quality, timeToContact: window.timeToContact } : null;
    },
    tick(dt: number, frame: InputFrame | null): void {
      tickCore(dt, frame, null);
    },
    tickMulti(dt: number, frames: ReadonlyMap<string, InputFrame>): void {
      tickCore(dt, null, frames);
    },
  };
}
